import { Component, DestroyRef, Input, OnInit, inject, signal } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ManagementService } from '../../management.service';
import { ManagementStore } from '../../management.store';
import { UtilService } from '../../../../services/util.service';
import { LinkedinTermRecord } from '../../types';
import { TitledInputComponent } from '../../../../shared/components/titled-input/titled-input.component';
import { SelectItem, SelectMenuComponent, SelectMode } from '../../../../shared/components/select-menu/select-menu.component';
import { ToggleSwitchComponent } from '../../../../shared/components/toggle-switch/toggle-switch.component';
import { DeleteBtnComponent } from '../../../../shared/components/delete-btn/delete-btn.component';

export type LinkedinTermFormGroup = FormGroup<{
  searchTerm: FormControl<string | null>;
  countries: FormControl<string[] | null>;
  isActive: FormControl<boolean | null>;
}>;

// The DAG-written fetch stats for an already-saved term, plus the term they belong to (so an edited
// row stops showing stale stats — see getFetchInfo).
interface LinkedinFetchInfo {
  searchTerm: string;
  lastFetchDate: string | null;
  lastAdsCount: number | null;
}

@Component({
  selector: 'app-linkedin-terms',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TitledInputComponent,
    SelectMenuComponent,
    ToggleSwitchComponent,
    DeleteBtnComponent,
  ],
  templateUrl: './linkedin-terms.component.html',
  styleUrl: './linkedin-terms.component.scss',
})
export class LinkedinTermsComponent implements OnInit {
  // The FormArray is owned by the parent's form, so submit()/getBrandPayload keep operating on one
  // form tree. This component only manages the rows and the country options. Unlike Meta pages there
  // is no validation: a search term is free text with no concept of "existence".
  @Input({ required: true }) array!: FormArray<LinkedinTermFormGroup>;
  // When set (edit mode), the brand's saved terms are loaded; otherwise a single empty row is seeded.
  @Input() existingBrandKey: string | null = null;

  public isExpanded = true;
  public countryOptions: SelectItem[] = [];
  // True once the existing terms have been established (loaded in edit mode, or immediately in add
  // mode). The parent only serializes this section into the brand payload when it is loaded AND the
  // FormArray is dirty, so a save fired before the async load returns can't wipe the brand's terms,
  // and an untouched section costs no Snowflake write at all.
  public readonly loaded = signal(false);
  // Set when loading the saved terms failed: the section is disabled (see loadTerms) and a banner
  // explains that saving won't change it — otherwise edits would just silently not persist.
  public readonly loadFailed = signal(false);
  protected readonly SelectMode = SelectMode;

  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);
  private managementService = inject(ManagementService);
  private store = inject(ManagementStore);
  private utilService = inject(UtilService);

  // Per-row DAG fetch stats, keyed by the row's searchTerm control. Read-only; shown only for terms
  // already saved (and only while the term is unchanged).
  private fetchInfo = new Map<AbstractControl, LinkedinFetchInfo>();

  ngOnInit(): void {
    // Mark the section dirty on any user change. Typing sets dirty by itself, but the country
    // select-menu and the active toggle write via programmatic setValue (which doesn't), and row
    // removal only emits on the array — one array-level subscription covers them all. Programmatic
    // row loading below always uses emitEvent:false, so this fires only for real user changes.
    this.array.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.array.markAsDirty());
    // Duplicate terms would silently collapse server-side (last one wins); surface them instead.
    this.array.addValidators(this.duplicateTermsValidator);
    this.initCountryOptions();
    if (this.existingBrandKey) {
      this.loadTerms(this.existingBrandKey);
    } else {
      this.terms.push(this.createEmptyRow(), {emitEvent: false});
      this.loaded.set(true);
    }
  }

  get terms(): FormArray<LinkedinTermFormGroup> {
    return this.array;
  }

  private loadTerms(brandKey: string): void {
    this.managementService.getBrandLinkedinTerms(brandKey)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: terms => {
          terms.forEach(term => this.terms.push(this.createPrefilledRow(term), {emitEvent: false}));
          this.terms.push(this.createEmptyRow(), {emitEvent: false});
          this.loaded.set(true);
        },
        error: () => {
          // Leave `loaded` false: we don't have the saved terms, so the parent omits linkedinTerms on
          // save rather than sending an empty list that would wipe them. Disable the section and show
          // the banner so edits can't be typed in and then silently dropped.
          this.loadFailed.set(true);
          this.terms.push(this.createEmptyRow(), {emitEvent: false});
          this.array.disable({emitEvent: false});
        }
      });
  }

  // Countries reuse the brand-scoped meta-page country list (same ISO alpha-2 set), fetched once per
  // session and cached in the store, so opening the LinkedIn section issues no extra request.
  private initCountryOptions(): void {
    this.store.getMetaPageCountries()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: countries => {
          this.countryOptions = countries.map(country => ({
            label: country.name,
            key: country.code,
            icon: this.utilService.getCountryFlagUrl(country.code)
          }));
        },
        error: () => { /* leave country options empty; the rest of the form still works */ }
      });
  }

  private createEmptyRow(): LinkedinTermFormGroup {
    return this.fb.group({
      searchTerm: new FormControl<string | null>(null),
      countries: new FormControl<string[]>([]),
      isActive: new FormControl<boolean>(true),
    }, { validators: [this.rowValidator] }) as LinkedinTermFormGroup;
  }

  private createPrefilledRow(term: LinkedinTermRecord): LinkedinTermFormGroup {
    const row = this.createEmptyRow();
    // emitEvent:false keeps the prefill from marking the section dirty (validators still run, silently).
    row.patchValue({ searchTerm: term.search_term, countries: term.countries, isActive: term.is_active }, {emitEvent: false});
    this.fetchInfo.set(row.get('searchTerm')!, { searchTerm: term.search_term, lastFetchDate: term.last_fetch_date, lastAdsCount: term.last_ads_count });
    return row;
  }

  // A row needs at least one country once it has a search term (mirrors the meta-page rule).
  private rowValidator = (group: AbstractControl): ValidationErrors | null => {
    const term = (group.get('searchTerm')?.value ?? '').trim();
    const countries = group.get('countries')?.value ?? [];
    if (term && countries.length === 0) {
      return { countriesRequired: true };
    }
    return null;
  };

  // Exact-match duplicates only: casing is meaningful to the search ('3M' and '3m' are legitimately
  // distinct terms in the real data), so only identical trimmed terms are flagged.
  private duplicateTermsValidator = (array: AbstractControl): ValidationErrors | null => {
    const terms = (array as FormArray).controls
      .map(row => ((row.get('searchTerm')?.value as string | null) ?? '').trim())
      .filter(Boolean);
    return new Set(terms).size !== terms.length ? { duplicateTerms: true } : null;
  };

  getSearchTermControl(index: number): FormControl<string | null> {
    return this.terms.at(index).get('searchTerm') as FormControl<string | null>;
  }

  getCountriesControl(index: number): FormControl<string[] | null> {
    return this.terms.at(index).get('countries') as FormControl<string[] | null>;
  }

  getIsActiveControl(index: number): FormControl<boolean | null> {
    return this.terms.at(index).get('isActive') as FormControl<boolean | null>;
  }

  // Stats belong to the term as loaded; once the user edits the term they're stale, so hide them.
  getFetchInfo(index: number): LinkedinFetchInfo | undefined {
    const info = this.fetchInfo.get(this.getSearchTermControl(index));
    if (!info) {
      return undefined;
    }
    const current = (this.getSearchTermControl(index).value ?? '').trim();
    return current === info.searchTerm ? info : undefined;
  }

  isRowCountriesMissing(index: number): boolean {
    return this.terms.at(index).hasError('countriesRequired');
  }

  // A row is flagged when its (non-empty) term also appears in an earlier or later row — shown under
  // every duplicate so the user sees which rows collide.
  isRowDuplicateTerm(index: number): boolean {
    const term = ((this.getSearchTermControl(index).value as string | null) ?? '').trim();
    if (!term) {
      return false;
    }
    return this.terms.controls.some((row, rowIndex) =>
      rowIndex !== index && ((row.get('searchTerm')?.value as string | null) ?? '').trim() === term);
  }

  isRowEmpty(index: number): boolean {
    const row = this.terms.at(index);
    return !row.get('searchTerm')?.value && (row.get('countries')?.value?.length ?? 0) === 0;
  }

  toggle(): void {
    this.isExpanded = !this.isExpanded;
  }

  removeRow(index: number): void {
    this.fetchInfo.delete(this.getSearchTermControl(index));
    this.terms.removeAt(index);
  }

  checkAndAddRow(index: number): void {
    if (index === this.terms.length - 1) {
      const row = this.terms.at(index);
      if (row.get('searchTerm')?.value && (row.get('countries')?.value?.length ?? 0) > 0) {
        // emitEvent:false — appending the trailing empty row isn't a data change and shouldn't mark dirty.
        this.terms.push(this.createEmptyRow(), {emitEvent: false});
      }
    }
  }
}
